import { UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  Signal,
  ResolvedRelevantParty,
  UpdateSignalApiRequest,
} from '@momentum/types';
import { SignalVisibilityType } from '@momentum/types';
import type { UsersAndTeams } from '@momentum/types';
import type { EmployeeInfo } from '../../constants';
import { AnonymousSelect } from './AnonymousSelect';
import { DetailRow } from './DetailRow';
import { MarketplaceVisibilitySelect } from './MarketplaceVisibilitySelect';
import { TagPill, TeamPill } from './PartyPill';
import { SeveritySelect } from './SeveritySelect';
import { VisibleToSelect } from './visible-to-select/VisibleToSelect';

interface SignalDetailSidebarProps {
  signal: Signal;
  severityInfo: { labelKey: string; color: string };
  author: EmployeeInfo | undefined;
  employeeMap: Map<string, EmployeeInfo>;
  teamMap: Map<string, string>;
  isPublic: boolean;
  resolvedRelevantParties?: ResolvedRelevantParty[];
  isCreator: boolean;
  onUpdate: (data: UpdateSignalApiRequest) => void;
}

export function SignalDetailSidebar({
  signal,
  severityInfo,
  author,
  employeeMap,
  teamMap,
  isPublic,
  resolvedRelevantParties,
  isCreator,
  onUpdate,
}: SignalDetailSidebarProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="flex flex-col bg-muted/30 px-[18px] py-6">
      {/* Properties section */}
      <div className="pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground/60">
        {t('propertiesSection')}
      </div>

      {/* Severity */}
      <SeveritySelect
        severity={signal.severity}
        severityInfo={severityInfo}
        isCreator={isCreator}
        onSeverityChange={(severity) => onUpdate({ severity })}
      />

      {/* Relevant parties */}
      <DetailRow label={t('relevantPartiesLabel')}>
        {resolvedRelevantParties && resolvedRelevantParties.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {resolvedRelevantParties.map((party) =>
              party.type === 'user' ? (
                <TagPill
                  key={party.id}
                  name={party.name}
                  imageUrl={employeeMap.get(party.id)?.imageUrl}
                />
              ) : (
                <TeamPill key={party.id} name={party.name} />
              )
            )}
          </div>
        ) : signal.relevantParties?.users?.length ||
          signal.relevantParties?.teams?.length ? (
          <div className="flex flex-wrap gap-1">
            {signal.relevantParties.users?.map((userId) => {
              const emp = employeeMap.get(userId);
              return emp ? (
                <TagPill
                  key={userId}
                  name={emp.fullName}
                  imageUrl={emp.imageUrl}
                />
              ) : null;
            })}
            {signal.relevantParties.teams?.map((teamId) => (
              <TeamPill
                key={teamId}
                name={teamMap.get(teamId) ?? t('detailUnknownTeam')}
              />
            ))}
          </div>
        ) : (
          <span className="text-[13px] text-muted-foreground">
            {t('detailNoRelevantParties')}
          </span>
        )}
      </DetailRow>

      {/* Author — creator always sees their name, others see anonymous or name */}
      <DetailRow label={t('detailAuthor')}>
        <div className="flex flex-wrap gap-1">
          {!isCreator && signal.isAnonymous ? (
            <div className="flex items-center gap-1.5 text-[13px] text-foreground">
              <UserX className="h-3.5 w-3.5 text-muted-foreground" />
              {t('anonymous')}
            </div>
          ) : author ? (
            <TagPill name={author.fullName} imageUrl={author.imageUrl} />
          ) : null}
        </div>
      </DetailRow>

      {/* Visibility section */}
      <div className="mt-1.5 border-t pt-3.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground/60">
        {t('visibilitySection')}
      </div>

      {/* Marketplace */}
      <MarketplaceVisibilitySelect
        visibilityType={signal.visibility.type}
        isPublic={isPublic}
        isCreator={isCreator}
        onVisibilityChange={(visibilityType) =>
          onUpdate({
            visibility: { type: visibilityType },
          })
        }
      />

      {/* Anonymous — creator only */}
      {isCreator && (
        <AnonymousSelect
          isAnonymous={signal.isAnonymous ?? false}
          onAnonymousChange={(isAnonymous) => onUpdate({ isAnonymous })}
        />
      )}

      {/* Visible to (private only) */}
      {signal.visibility.type === SignalVisibilityType.PRIVATE && (
        <div className="px-2.5 py-2 -mx-2.5">
          <div className="text-xs font-medium text-muted-foreground mb-1.5">
            {t('visibleToLabel')}
          </div>
          <VisibleToSelect
            value={signal.visibility.viewableBy ?? { users: [], teams: [] }}
            installed={signal.installed}
            isCreator={isCreator}
            employeeMap={employeeMap}
            teamMap={teamMap}
            onChange={(viewableBy: UsersAndTeams) =>
              onUpdate({
                visibility: {
                  type: SignalVisibilityType.PRIVATE,
                  viewableBy,
                },
              })
            }
          />
        </div>
      )}
    </div>
  );
}
